import React, { createContext, useState } from "react";

import { Story } from "chq";
import { useStoriesStack } from "app/context/StoriesStackContext";
import { noSlackBroadcastValue } from "components/event/edit/SlackChannelField";
import { EMPTY_EDITOR_TEXT } from "app/utils/constants";

interface StoryContextProps {
  children: React.ReactNode;
}

export interface StoryModalContextType {
  activeStory?: Story;
  appending: number | null;
  changeModalIsOpen: (value: boolean) => void;
  changeSelectedBody: (value: string) => void;
  changeSelectedChannel: (value: string) => void;
  changeSelectedOptions: (values: Record<string, any>[]) => void;
  columnWidth: string;
  fetching: boolean;
  handleThumbnailClick: (index: number) => void;
  isAppendEnable: boolean;
  isControlledModeStories: boolean;
  modalIsOpen: boolean;
  onActiveStory: (activeStory: Story) => void;
  onAppendStart: () => void;
  onSelectFilters: (selectedFilters: any[]) => void;
  onSelectStories: (selectedStories: Story[], data?: { append?: boolean }) => void;
  selectedChannel: string;
  selectedBody: string;
  selectedFilters: any[];
  selectedOptions: any[];
  selectedStories: Story[];
  stories: Story[];
}

export const StoryModalContext = createContext<StoryModalContextType>({
  activeStory: undefined,
  appending: null,
  changeModalIsOpen: (_value: boolean) => {},
  changeSelectedBody: (_value: string) => {},
  changeSelectedChannel: (_value: string) => {},
  changeSelectedOptions: (_values: Record<string, any>[]) => {},
  columnWidth: "50%",
  fetching: false,
  handleThumbnailClick: () => {},
  isAppendEnable: false,
  isControlledModeStories: false,
  modalIsOpen: false,
  onActiveStory: (_activeStory: Story) => {},
  onAppendStart: () => {},
  onSelectFilters: (_selectedFilters: any[]) => {},
  onSelectStories: (_selectedStories: Story[]) => {},
  selectedChannel: noSlackBroadcastValue,
  selectedBody: EMPTY_EDITOR_TEXT,
  selectedFilters: [],
  selectedOptions: [],
  selectedStories: [],
  stories: []
});

const StoryContext: React.FC<StoryContextProps> = ({
  children
}) => {
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedBody, setSelectedBody] = useState(EMPTY_EDITOR_TEXT);
  const [selectedChannel, setSelectedChannel] = useState(noSlackBroadcastValue);
  const [selectedOptions, setSelectedOptions] = useState<Record<string, any>[]>([]);

  const {
    isAppendEnable,
    isControlledMode: isControlledModeStories,
    state: {
      activeStory, appending, columnWidth, fetching, stories, selectedFilters, selectedStories
    },
    onActiveStory,
    onAppendStart,
    onSelectFilters,
    onSelectStories
  } = useStoriesStack();

  const handleThumbnailClick = (goToIndex: number) => {
    let target = goToIndex;

    if (goToIndex === -1) {
      target = stories.length - 1;
    }

    if (goToIndex > stories.length - 1) {
      target = 0;
    }

    onActiveStory(stories[target]);
    setModalIsOpen(true);
  };

  const changeModalIsOpen = (value: boolean) => setModalIsOpen(value);
  const changeSelectedBody = (value: string) => setSelectedBody(value);
  const changeSelectedChannel = (channel: string) => setSelectedChannel(channel);
  const changeSelectedOptions = (values: Record<string, any>[]) => setSelectedOptions(values);

  return (
    <StoryModalContext.Provider
      value={{
        activeStory,
        appending,
        changeModalIsOpen,
        changeSelectedBody,
        changeSelectedChannel,
        changeSelectedOptions,
        columnWidth,
        fetching,
        handleThumbnailClick,
        isAppendEnable,
        isControlledModeStories,
        modalIsOpen,
        onActiveStory,
        onAppendStart,
        onSelectFilters,
        onSelectStories,
        selectedBody,
        selectedChannel,
        selectedFilters,
        selectedOptions,
        selectedStories,
        stories
      }}
    >
      {children}
    </StoryModalContext.Provider>
  );
};

export default StoryContext;
